import React, { useEffect, useRef, useState } from "react";
import useModal from "hooks/useModal";
import { useTranslation } from "react-i18next";
import dynamic from "next/dynamic";
import { useSettings } from "contexts/settings/settings.context";
import { getAddressFromLocation } from "utils/getAddressFromLocation";
import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import { useAuth } from "contexts/auth/auth.context";
import addressService from "services/address";
import { IAddress } from "interfaces/address.interface";
import MapPin2FillIcon from "remixicon-react/MapPin2FillIcon";
import cls from "./addressContainer.module.scss";
import Loading from "components/loader/loading";

const AddressModal = dynamic(
  () => import("components/addressModal/addressModal"),
);
const ModalContainer = dynamic(() => import("containers/modal/modal"));
const MobileDrawer = dynamic(() => import("containers/drawer/mobileDrawer"));
const SavedAddressList = dynamic(() => import("./savedAddressList"), {
  loading: () => <Loading />,
});

type Props = {};

export default function AddressContainer({}: Props) {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:1140px)");
  const { query } = useRouter();
  const { settings, address, location, updateAddress, updateLocation } =
    useSettings();
  const { isAuthenticated, user } = useAuth();
  const addressRef = useRef<any>();
  const [
    savedAddressesListModal,
    handleOpenSavedAddressesListModal,
    handleCloseSavedAddressesListModal,
  ] = useModal();
  const [addressModal, handleOpenAddressModal, handleCloseAddressModal] =
    useModal();
  const [
    savedAddressList,
    handleOpenSavedAddressList,
    handleCloseSavedAddressList,
  ] = useModal();
  const [userAddress, setUserAddress] = useState<string | undefined>(undefined);
  const [userLocation, setUserLocation] = useState("");
  const [editedAddress, setEditedAddress] = useState<IAddress | null>(null);

  const generalSettingsDefaultLocation = {
    latitude: settings?.generalSettingsLocation?.split(", ")?.[0],
    longitude: settings?.generalSettingsLocation?.split(", ")?.[1],
  };

  const { data: addresses } = useQuery(
    ["addresses", isAuthenticated, user?.id],
    () =>
      addressService.getAll({
        perPage: 100,
      }),
    {
      onSuccess: (res) => {
        if (res.length !== 0) {
          const defaultAddress = res.find((item) => Boolean(item.active));
          let latlng: string;
          if (defaultAddress?.location) {
            latlng = `${defaultAddress?.location.at(0)},${defaultAddress.location.at(1)}`;
          } else {
            latlng = location;
          }
          setUserAddress(defaultAddress?.address?.address || "");
          updateAddress(defaultAddress?.address?.address);
          updateLocation(latlng);
        }
      },
      enabled: isAuthenticated,
    },
  );

  useEffect(() => {
    if (!address) {
      window.navigator.geolocation.getCurrentPosition(
        defineLocation,
        defineLocation,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  async function defineLocation(position: any) {
    const { coords } = position;
    let latlng: string;
    if (coords) {
      latlng = `${coords.latitude},${coords.longitude}`;
    } else {
      if (
        generalSettingsDefaultLocation.latitude &&
        generalSettingsDefaultLocation.longitude
      ) {
        latlng = `${generalSettingsDefaultLocation.latitude},${generalSettingsDefaultLocation.longitude}`;
      } else {
        latlng = location;
      }
    }

    const addr = await getAddressFromLocation(latlng);
    setUserAddress(addr);
    setUserLocation(latlng);
    updateLocation(latlng);
    updateAddress(addr);
    try {
      addressRef.current.click();
    } catch (err) {
      console.log("err => ", err);
    }
    if (query.g) {
      updateAddress(addr);
    }
  }

  const handleClickAddressRef = (event: any) => {
    event.stopPropagation();
    switch (isAuthenticated) {
      case false:
        handleOpenAddressModal();
        break;
      case true:
        if (addresses?.length) {
          if (isDesktop) {
            handleOpenSavedAddressesListModal();
          } else {
            handleOpenSavedAddressList(event);
          }
        } else {
          handleOpenAddressModal();
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <button
        type="button"
        className={cls.address}
        onClick={(e) =>
          isAuthenticated ? handleClickAddressRef(e) : handleOpenAddressModal()
        }
      >
        <div className={cls.icon}>
          <MapPin2FillIcon size={20} />
        </div>
        <div
          ref={addressRef}
          onClick={handleClickAddressRef}
          className={cls.addressTitle}
        >
          <label>{t("delivery.address")}</label>
          <p>{address}</p>
        </div>
      </button>
      {isDesktop ? (
        <ModalContainer
          open={savedAddressesListModal}
          onClose={handleCloseSavedAddressesListModal}
          PaperProps={{
            style: {
              width: "730px",
            },
          }}
        >
          <SavedAddressList
            handleOpenAddressModal={handleOpenAddressModal}
            addresses={addresses}
            handleCloseList={handleCloseSavedAddressesListModal}
            onSelectAddress={(value) => {
              setEditedAddress(value);
              handleOpenAddressModal();
            }}
          />
        </ModalContainer>
      ) : (
        <MobileDrawer
          open={savedAddressList}
          onClose={handleCloseSavedAddressList}
        >
          <SavedAddressList
            handleOpenAddressModal={handleOpenAddressModal}
            addresses={addresses}
            handleCloseList={handleCloseSavedAddressList}
            onSelectAddress={(value) => {
              setEditedAddress(value);
              handleCloseSavedAddressList();
              handleOpenAddressModal();
            }}
          />
        </MobileDrawer>
      )}
      {addressModal && (
        <AddressModal
          open={addressModal}
          onClose={() => {
            handleCloseAddressModal();
            setEditedAddress(null);
          }}
          latlng={editedAddress?.location.join(",") || userLocation || location}
          address={editedAddress?.address?.address || userAddress || address}
          fullScreen={!isDesktop}
          editedAddress={editedAddress}
          onClearAddress={() => setEditedAddress(null)}
        />
      )}
    </>
  );
}
